import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Anchor, Flex, Text, Title } from "@mantine/core";

interface SectionHeaderProps {
    title: ReactNode;
    /** For `aria-labelledby` on the section the header belongs to. */
    id?: string;
    /** Small dimmed line next to the title, such as a count or a hint. */
    note?: ReactNode;
    /** A "see all" link, shown at the end of the row. */
    action?: { label: string; to: string };
    /**
     * `section` is a page section heading with its note and link in a row.
     * `panel` is the smaller heading for a card or form block.
     */
    variant?: "section" | "panel";
}

/**
 * The heading above a block of content. One place decides the heading level, size
 * and how the note and the "see all" link line up.
 */
export function SectionHeader({ title, id, note, action, variant = "section" }: SectionHeaderProps) {
    const heading =
        variant === "panel" ? (
            <Title id={id} order={2} size="h4">
                {title}
            </Title>
        ) : (
            <Title id={id} order={2}>
                {title}
            </Title>
        );
    if (variant === "panel" && !action && !note) return heading;
    return (
        <Flex align="baseline" columnGap="md" justify="space-between" mb="md" rowGap="xs" wrap="wrap">
            {heading}
            {note && (
                <Text c="dimmed" size="sm">
                    {note}
                </Text>
            )}
            {action && (
                <Anchor component={Link} size="sm" to={action.to}>
                    {action.label}
                </Anchor>
            )}
        </Flex>
    );
}
